/**
 * Global error handler for uncaught errors and promise rejections
 * This helps capture errors that might not be caught by React's Error Boundary
 */

import { errorLogger } from './logger';

let errorDisplay: HTMLDivElement | null = null;

interface ErrorDetails {
  message: string;
  stack?: string;
  type: 'error' | 'unhandledrejection';
  timestamp: string;
}

function createErrorDisplay(error: ErrorDetails) {
  // Log error using pino
  errorLogger.error({
    type: error.type,
    message: error.message,
    stack: error.stack,
    userAgent: navigator.userAgent,
    timestamp: error.timestamp
  }, 'Global uncaught error');

  // Remove existing error display if any
  if (errorDisplay) {
    document.body.removeChild(errorDisplay);
  }

  // Create error display
  errorDisplay = document.createElement('div');
  errorDisplay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #fee2e2 0%, #fed7aa 100%);
    z-index: 999999;
    padding: 20px;
    overflow-y: auto;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  `;

  const errorText = `
Error Type: ${error.type}
Message: ${error.message}
Stack: ${error.stack || 'No stack trace available'}
User Agent: ${navigator.userAgent}
Timestamp: ${error.timestamp}
  `.trim();

  errorDisplay.innerHTML = `
    <div style="max-width: 800px; margin: 0 auto; background: white; border-radius: 12px; padding: 24px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); animation: slideIn 0.3s ease-out;">
      <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 20px; padding-bottom: 20px; border-bottom: 2px solid #fee2e2;">
        <div style="display: flex; align-items: center; gap: 12px;">
          <svg style="width: 32px; height: 32px; color: #dc2626; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          <div>
            <h1 style="margin: 0; font-size: 24px; font-weight: 700; color: #991b1b;">Something went wrong</h1>
            <p style="margin: 4px 0 0 0; font-size: 14px; color: #b91c1c;">The app encountered an unexpected error</p>
          </div>
        </div>
        <button id="dismiss-error-btn" style="padding: 8px; background: transparent; border: none; border-radius: 6px; cursor: pointer; color: #dc2626; display: flex; align-items: center; justify-content: center; transition: background 0.2s;" onmouseover="this.style.background='#fef2f2'" onmouseout="this.style.background='transparent'">
          <svg style="width: 20px; height: 20px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <div style="margin-bottom: 16px;">
        <h2 style="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: #111827;">Error Message:</h2>
        <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 12px;">
          <code style="font-size: 13px; color: #991b1b; word-break: break-word;">${error.message}</code>
        </div>
      </div>

      ${error.stack ? `
        <div style="margin-bottom: 16px;">
          <h2 style="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: #111827;">Stack Trace:</h2>
          <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; max-height: 200px; overflow-y: auto;">
            <pre style="margin: 0; font-size: 11px; color: #374151; white-space: pre-wrap; word-break: break-word; font-family: monospace;">${error.stack}</pre>
          </div>
        </div>
      ` : ''}

      <div style="display: flex; gap: 12px; flex-wrap: wrap;">
        <button id="copy-error-btn" style="flex: 1; min-width: 150px; padding: 12px 20px; background: white; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; font-weight: 500; color: #374151; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;">
          <svg style="width: 16px; height: 16px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
          </svg>
          <span>Copy Error Details</span>
        </button>
        <button id="reload-app-btn" style="flex: 1; min-width: 150px; padding: 12px 20px; background: #dc2626; border: none; border-radius: 8px; font-size: 14px; font-weight: 500; color: white; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;">
          <svg style="width: 16px; height: 16px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          <span>Reload App</span>
        </button>
      </div>

      <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
        <p style="margin: 0; font-size: 12px; color: #6b7280;">
          <strong>Tip:</strong> Copy the error details and share them with support. Reloading the app may resolve the issue.
        </p>
      </div>
    </div>
  `;

  document.body.appendChild(errorDisplay);

  // Add CSS keyframes for animation
  if (!document.getElementById('error-handler-styles')) {
    const style = document.createElement('style');
    style.id = 'error-handler-styles';
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateY(-20px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // Add event listeners
  const copyBtn = document.getElementById('copy-error-btn');
  const reloadBtn = document.getElementById('reload-app-btn');
  const dismissBtn = document.getElementById('dismiss-error-btn');

  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      errorLogger.info('User dismissed global error');
      if (errorDisplay && errorDisplay.parentNode) {
        errorDisplay.style.opacity = '0';
        errorDisplay.style.transition = 'opacity 0.3s';
        setTimeout(() => {
          if (errorDisplay && errorDisplay.parentNode) {
            document.body.removeChild(errorDisplay);
          }
          errorDisplay = null;
        }, 300);
      }
    });
  }

  if (copyBtn) {
    copyBtn.addEventListener('click', () => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(errorText).then(() => {
          const span = copyBtn.querySelector('span');
          if (span) {
            const originalText = span.textContent;
            span.textContent = 'Copied!';
            setTimeout(() => {
              span.textContent = originalText;
            }, 2000);
          }
        }).catch(() => {
          fallbackCopy(errorText);
        });
      } else {
        fallbackCopy(errorText);
      }
    });
  }

  if (reloadBtn) {
    reloadBtn.addEventListener('click', () => {
      errorLogger.info('User requested app reload from global error handler');
      window.location.reload();
    });
  }
}

function fallbackCopy(text: string) {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand('copy');
    alert('Error details copied to clipboard!');
  } catch (err) {
    alert('Failed to copy. Please take a screenshot.');
  }
  document.body.removeChild(textArea);
}

export function initializeErrorHandlers() {
  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    createErrorDisplay({
      message: event.error?.message || event.message || 'Unknown error',
      stack: event.error?.stack,
      type: 'error',
      timestamp: new Date().toISOString(),
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    createErrorDisplay({
      message: event.reason?.message || String(event.reason) || 'Unhandled promise rejection',
      stack: event.reason?.stack,
      type: 'unhandledrejection',
      timestamp: new Date().toISOString(),
    });
  });

  console.log('Global error handlers initialized');
}
